/**
 * Referral attachments that upload in the background while the user keeps filling in the form.
 *
 * Each picked file becomes a tile straight away and uploads through the shared helper
 * (src/lib/referralAttachmentUpload.ts) — at most REFERRAL_UPLOAD_CONCURRENCY at once across every
 * batch. A tile can be retried when it fails and removed at any time; removing an upload that is
 * still running aborts it, and removing a finished one deletes the file from storage. Only
 * finished uploads appear in `completed`, which has the same shape the referral endpoints have
 * always taken.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { describeUploadError } from '@/lib/mediaUploadValidation';
import {
  createConcurrencyLimiter,
  discardReferralAttachment,
  REFERRAL_ATTACHMENTS_BUCKET,
  REFERRAL_UPLOAD_CONCURRENCY,
  stabilizeAttachmentFile,
  UploadAbortedError,
  uploadReferralAttachmentFile,
} from '@/lib/referralAttachmentUpload';

/** What the referral create endpoints expect for each attachment. */
export interface ReferralAttachmentPayload {
  name: string;
  path: string;
  bucket: string;
  size: number;
  type: string;
  url?: string;
}

export type PendingAttachmentStatus = 'uploading' | 'done' | 'failed';

export interface PendingAttachment {
  id: string;
  name: string;
  kind: 'image' | 'video';
  /** Local object URL for an image thumbnail; videos show an icon instead. */
  previewUrl?: string;
  status: PendingAttachmentStatus;
  /** 0–1 while the bytes are moving; null when unknown (preparing, or the proxied fallback). */
  progress: number | null;
  /** Why it failed, ready to show (describeUploadError). */
  error?: string;
  uploaded?: ReferralAttachmentPayload;
}

export interface NewAttachment {
  file: File;
  kind: 'image' | 'video';
  /** Runs once before the first upload attempt, e.g. compressing an oversized photo. */
  prepare?: (file: File) => Promise<File>;
}

export interface UploadOutcome {
  id: string;
  name: string;
  status: 'done' | 'failed' | 'removed';
  error?: string;
}

export interface UploadSummary {
  total: number;
  done: number;
  uploading: number;
  failed: number;
}

/** Whether a submit can go ahead now, has to wait for uploads, or is blocked by failed ones. */
export type SubmitGate = 'ready' | 'waiting' | 'failed';

export function summarizeUploads(items: readonly Pick<PendingAttachment, 'status'>[]): UploadSummary {
  const summary: UploadSummary = { total: items.length, done: 0, uploading: 0, failed: 0 };
  for (const item of items) {
    if (item.status === 'done') summary.done++;
    else if (item.status === 'uploading') summary.uploading++;
    else summary.failed++;
  }
  return summary;
}

/**
 * Uploads still running → wait (a failure among them is reported once they all settle, so the
 * user fixes everything in one go). Nothing running but something failed → blocked.
 */
export function getSubmitGate(summary: UploadSummary): SubmitGate {
  if (summary.uploading > 0) return 'waiting';
  if (summary.failed > 0) return 'failed';
  return 'ready';
}

let nextId = 0;

export function useBackgroundAttachmentUploads() {
  const [items, setItems] = useState<PendingAttachment[]>([]);

  // Everything an upload attempt needs that is not rendered. Keyed by item id.
  const sources = useRef(
    new Map<string, { file: File; prepare?: NewAttachment['prepare']; stabilized?: boolean }>()
  );
  const controllers = useRef(new Map<string, AbortController>());
  const limiter = useRef(createConcurrencyLimiter(REFERRAL_UPLOAD_CONCURRENCY)).current;

  const update = useCallback((id: string, patch: Partial<PendingAttachment>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const start = useCallback(
    (id: string, name: string): Promise<UploadOutcome> => {
      const controller = new AbortController();
      controllers.current.set(id, controller);
      update(id, { status: 'uploading', progress: null, error: undefined });

      return limiter(async () => {
        if (controller.signal.aborted) throw new UploadAbortedError();
        const source = sources.current.get(id);
        if (!source) throw new UploadAbortedError();

        // Copy the picked file into memory before anything reads it (Android's photo picker hands
        // over handles that stop being readable; see stabilizeAttachmentFile). The thumbnail is
        // redrawn from the copy for the same reason.
        if (!source.stabilized) {
          source.file = await stabilizeAttachmentFile(source.file);
          source.stabilized = true;
          const stableFile = source.file;
          setItems((prev) =>
            prev.map((item) => {
              if (item.id !== id || item.kind !== 'image') return item;
              if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
              return { ...item, previewUrl: URL.createObjectURL(stableFile) };
            })
          );
        }

        if (source.prepare) {
          // Keep the prepared file so a retry does not redo the work.
          source.file = await source.prepare(source.file);
          source.prepare = undefined;
        }
        const file = source.file;
        const result = await uploadReferralAttachmentFile(file, {
          signal: controller.signal,
          onProgress: ({ loaded, total }) => update(id, { progress: total > 0 ? loaded / total : null }),
        });
        return { file, result };
      })
        .then(({ file, result }): UploadOutcome => {
          // Removed while the last bytes were landing: the file is stored but nobody wants it.
          if (controller.signal.aborted) {
            discardReferralAttachment(result.path);
            return { id, name, status: 'removed' };
          }
          update(id, {
            status: 'done',
            progress: 1,
            uploaded: {
              name: file.name,
              path: result.path,
              bucket: REFERRAL_ATTACHMENTS_BUCKET,
              size: file.size,
              type: file.type,
              url: result.url,
            },
          });
          return { id, name, status: 'done' };
        })
        .catch((error: unknown): UploadOutcome => {
          if (error instanceof UploadAbortedError || controller.signal.aborted) {
            return { id, name, status: 'removed' };
          }
          const reason = describeUploadError(
            error instanceof ApiError ? error : { message: error instanceof Error ? error.message : undefined },
            name
          );
          update(id, { status: 'failed', progress: null, error: reason });
          return { id, name, status: 'failed', error: reason };
        })
        .finally(() => {
          if (controllers.current.get(id) === controller) controllers.current.delete(id);
        });
    },
    [limiter, update]
  );

  /** Add tiles for these files right away and upload them in the background. */
  const addFiles = useCallback(
    (files: readonly NewAttachment[]): Promise<UploadOutcome[]> => {
      const added = files.map(({ file, kind, prepare }): PendingAttachment => {
        const id = `att-${++nextId}`;
        sources.current.set(id, { file, prepare });
        return {
          id,
          name: file.name,
          kind,
          previewUrl: kind === 'image' ? URL.createObjectURL(file) : undefined,
          status: 'uploading',
          progress: null,
        };
      });
      setItems((prev) => [...prev, ...added]);
      return Promise.all(added.map((item) => start(item.id, item.name)));
    },
    [start]
  );

  const retry = useCallback(
    (id: string): Promise<UploadOutcome> | undefined => {
      const item = items.find((i) => i.id === id);
      if (!item || item.status !== 'failed') return undefined;
      return start(id, item.name);
    },
    [items, start]
  );

  const itemsRef = useRef(items);
  itemsRef.current = items;

  /** Drop a tile. An upload still in flight is cancelled; a finished one is deleted from storage. */
  const remove = useCallback((id: string) => {
    const uploaded = itemsRef.current.find((i) => i.id === id)?.uploaded;
    if (uploaded) discardReferralAttachment(uploaded.path);
    controllers.current.get(id)?.abort();
    controllers.current.delete(id);
    sources.current.delete(id);
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  // Leaving the page cancels whatever is still uploading and frees the thumbnails.
  useEffect(
    () => () => {
      controllers.current.forEach((controller) => controller.abort());
      itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
    },
    []
  );

  const summary = useMemo(() => summarizeUploads(items), [items]);
  const completed = useMemo(
    () => items.flatMap((item) => (item.status === 'done' && item.uploaded ? [item.uploaded] : [])),
    [items]
  );

  // Reloading or closing the tab mid-upload would lose the files — ask the browser to confirm.
  useEffect(() => {
    if (summary.uploading === 0) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [summary.uploading]);

  return { items, completed, summary, addFiles, retry, remove };
}
